import { db } from "../firebase";
import { collection, addDoc, getDocs, doc, updateDoc, getDoc, query, where, deleteDoc } from "firebase/firestore";

const fetchAllProjects = async () => {
  const querySnapshot = await getDocs(collection(db, "projects"));
  const projects = [];
  querySnapshot.forEach((doc) => {
    projects.push({ _id: doc.id, ...doc.data() });
  });
  return projects;
};

const fetchMyProjects = async (userId) => {
  // Fetch projects assigned specifically to the user
  const q = query(collection(db, "projects"), where("assignedTo", "==", userId));
  const querySnapshot = await getDocs(q);
  const projects = [];
  querySnapshot.forEach((doc) => {
    projects.push({ _id: doc.id, ...doc.data() });
  });
  return projects;
};

const fetchAvailableProjects = async () => {
  // 1. Fetch Open projects (unassigned)
  const q1 = query(
    collection(db, "projects"),
    where("status", "==", "Open"),
    where("assignedTo", "==", null)
  );
  const querySnapshot1 = await getDocs(q1);

  // 2. Fetch projects assigned to 'ALL' (Marketplace items)
  const q2 = query(
    collection(db, "projects"),
    where("assignedTo", "==", "ALL")
  );
  const querySnapshot2 = await getDocs(q2);

  const projects = [];
  querySnapshot1.forEach((doc) => {
    projects.push({ _id: doc.id, ...doc.data() });
  });
  querySnapshot2.forEach((doc) => {
    // Avoid duplicates if any
    if (!projects.some(p => p._id === doc.id)) {
      projects.push({ _id: doc.id, ...doc.data() });
    }
  });
  return projects;
};

const fetchProjectById = async (id) => {
  const docRef = doc(db, "projects", id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { _id: docSnap.id, ...docSnap.data() };
  } else {
    throw new Error("Project not found");
  }
};

const createProject = async (projectData) => {
  const docRef = await addDoc(collection(db, "projects"), {
    ...projectData,
    createdAt: new Date().toISOString(),
    status: 'Open',
    assignedTo: null,
    assignedToName: ""
  });
  return { _id: docRef.id, ...projectData };
};

const assignFreelancer = async (projectId, freelancerId, freelancerName) => {
  const projectRef = doc(db, "projects", projectId);
  await updateDoc(projectRef, {
    assignedTo: freelancerId,
    assignedToName: freelancerName,
    status: 'Assigned'
  });
  return { message: "Freelancer assigned" };
};

const assignFreelancerClone = async (projectId, freelancerId, freelancerName) => {
  // 1. Fetch original project
  const originalProject = await fetchProjectById(projectId);

  // 2. Clone it for the user
  const { _id, ...projectData } = originalProject;

  // Create new project for this user
  await addDoc(collection(db, "projects"), {
    ...projectData,
    assignedTo: freelancerId,
    assignedToName: freelancerName,
    status: 'Assigned',
    originalProjectId: projectId, // Link back to parent
    clonedAt: new Date().toISOString()
  });

  return { message: "Project cloned and assigned" };
};

const updateProjectStatus = async (projectId, status) => {
  const projectRef = doc(db, "projects", projectId);
  await updateDoc(projectRef, { status });
  return { message: "Status updated" };
};

const deleteProject = async (projectId) => {
  const projectRef = doc(db, "projects", projectId);
  await deleteDoc(projectRef);
  return { message: "Project deleted" };
};

const requestProject = async (projectId, userId, userName) => {
  const requestRef = await addDoc(collection(db, "project_requests"), {
    projectId,
    userId,
    userName,
    status: 'pending',
    requestedAt: new Date().toISOString()
  });
  return { id: requestRef.id };
};

const fetchUserRequests = async (userId) => {
  const q = query(collection(db, "project_requests"), where("userId", "==", userId));
  const querySnapshot = await getDocs(q);
  const requests = [];
  querySnapshot.forEach((doc) => {
    requests.push({ _id: doc.id, ...doc.data() });
  });
  return requests;
};

const fetchProjectRequests = async (projectId) => {
  const q = query(collection(db, "project_requests"), where("projectId", "==", projectId));
  const querySnapshot = await getDocs(q);
  const requests = [];
  querySnapshot.forEach((doc) => {
    requests.push({ _id: doc.id, ...doc.data() });
  });
  return requests;
};

const fetchAllRequests = async () => {
  const querySnapshot = await getDocs(collection(db, "project_requests"));
  const requests = [];
  querySnapshot.forEach((doc) => {
    requests.push({ _id: doc.id, ...doc.data() });
  });
  return requests;
};

const updateRequestStatus = async (requestId, status) => {
  const requestRef = doc(db, "project_requests", requestId);
  await updateDoc(requestRef, { status });
  return { message: "Request status updated" };
};

const projectService = {
  fetchAllProjects,
  fetchMyProjects,
  fetchAvailableProjects,
  fetchProjectById,
  createProject,
  assignFreelancer,
  assignFreelancerClone,
  updateProjectStatus,
  deleteProject,
  requestProject,
  fetchUserRequests,
  fetchProjectRequests,
  fetchAllRequests,
  updateRequestStatus
};

export default projectService;
